import {
  BankBranchesSelect,
  BanksSelect,
} from '@/components/BankAccountSelect';
import { FormSelectProps } from '@moneyforward/ap-frontend-components';
import { Dispatch, memo, SetStateAction, useMemo } from 'react';
import { FieldPath, FieldValues } from 'react-hook-form';

export type Props<TFieldValues extends FieldValues> = {
  bankAccountNameInputId: string;
  bankAccountName: FieldPath<TFieldValues>;
  bankAccountBranchInputId: string;
  bankAccountBranchName: FieldPath<TFieldValues>;
  getPopupContainer?: FormSelectProps<TFieldValues>['selectProps']['getPopupContainer'];
  setDraftInstantPayeeBankName?: Dispatch<SetStateAction<string | undefined>>;
  setDraftInstantPayeeBankBranchName?: Dispatch<
    SetStateAction<string | undefined>
  >;
  bankNameMismatchMessage?: string;
  bankBranchMismatchMessage?: string;
  onBankNameBlur?: () => void;
  onBankBranchBlur?: () => void;
} & Omit<
  FormSelectProps<TFieldValues>,
  'selectProps' | 'label' | 'inputId' | 'name' | 'onChangeAfter'
>;

export const _BankAccountSelect = <TFieldValues extends FieldValues>({
  bankAccountNameInputId,
  bankAccountName,
  bankAccountBranchInputId,
  bankAccountBranchName,
  rules,
  control,
  getPopupContainer,
  setDraftInstantPayeeBankName,
  setDraftInstantPayeeBankBranchName,
  bankNameMismatchMessage,
  bankBranchMismatchMessage,
  onBankNameBlur,
  onBankBranchBlur,
  ...rest
}: Props<TFieldValues>) => {
  const memorizedSelectProps: {
    getPopupContainer: FormSelectProps<TFieldValues>['selectProps']['getPopupContainer'];
  } = useMemo(() => ({ getPopupContainer }), [getPopupContainer]);
  return (
    <>
      <BanksSelect<TFieldValues>
        inputId={bankAccountNameInputId}
        rules={rules}
        name={bankAccountName}
        bankBranchName={bankAccountBranchName}
        control={control}
        selectProps={memorizedSelectProps}
        setDraftInstantPayeeBankName={setDraftInstantPayeeBankName}
        mismatchMessage={bankNameMismatchMessage}
        {...rest}
        onChangeAfter={onBankNameBlur}
      />
      <BankBranchesSelect<TFieldValues>
        inputId={bankAccountBranchInputId}
        rules={rules}
        name={bankAccountBranchName}
        control={control}
        bankIdName={bankAccountName}
        selectProps={memorizedSelectProps}
        setDraftInstantPayeeBankBranchName={setDraftInstantPayeeBankBranchName}
        mismatchMessage={bankBranchMismatchMessage}
        onChangeAfter={onBankBranchBlur}
      />
    </>
  );
};
_BankAccountSelect.displayName = '_BankAccountSelect';

// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
export const BankAccountSelect = memo((props) => (
  <_BankAccountSelect {...props} />
)) as typeof _BankAccountSelect;
BankAccountSelect.displayName = 'BankAccountSelect';
